<template>
  <div class="word-basics-page">
    <!-- 页面头部 -->
    <div class="page-header">
      <h1 class="page-title">
        <i class="el-icon-edit-outline"></i>
        单词基础
      </h1>
      <p class="page-subtitle">以单词为核心，对单词、句子等进行基础与提高学习</p>
    </div>

    <!-- 课程选择 -->
    <div class="course-selection">
      <div class="course-info">
        <h2>{{ selectedCourse.name }}</h2>
        <p>{{ selectedCourse.description }}</p>
          </div>

      <div class="course-switcher">
        <el-select v-model="selectedCourseId" @change="handleCourseChange" placeholder="选择学习阶段">
          <el-option
            v-for="course in courses"
            :key="course.id"
            :label="course.name"
            :value="course.id"
            :disabled="!course.isUnlocked"
          >
            <span style="float: left">{{ course.name }}</span>
            <span style="float: right; color: #8492a6; font-size: 13px">
              {{ course.isUnlocked ? '已解锁' : '未解锁' }}
            </span>
          </el-option>
            </el-select>
      </div>
          </div>

    <!-- 综合学习区域 -->
    <div class="comprehensive-learning">
      <div class="section-title">
        <div class="title-bar"></div>
        <h3>综合学习</h3>
      </div>
      
      <div class="learning-options">
        <div class="option-card" @click="startSmartDictation">
          <div class="option-icon">
            <i class="el-icon-microphone"></i>
          </div>
          <div class="option-content">
            <h4>智能听写</h4>
            <p>根据遗忘曲线智能安排听写练习</p>
          </div>
        </div>

        <div class="option-card" @click="startComparisonTest">
          <div class="option-icon">
            <i class="el-icon-sort"></i>
          </div>
          <div class="option-content">
            <h4>对比测试</h4>
            <p>对比相似单词，提高辨析能力</p>
          </div>
      </div>

        <div class="option-card" @click="startEndToEndTest">
          <div class="option-icon">
            <i class="el-icon-trophy"></i>
          </div>
          <div class="option-content">
            <h4>一测到底</h4>
            <p>连续测试直到全部掌握</p>
          </div>
          </div>

        <div class="option-card" @click="startAICloze">
          <div class="option-icon">
            <i class="el-icon-magic-stick"></i>
          </div>
          <div class="option-content">
            <h4>AI完形</h4>
            <p>AI生成的完形填空练习</p>
          </div>
          </div>
        </div>
      </div>

    <!-- 单元学习区域 -->
    <div class="unit-learning">
      <div class="section-title">
        <div class="title-bar"></div>
        <h3>单词学习 | 单元测试</h3>
      </div>
      
      <div class="units-grid" v-loading="loading">
        <div 
          v-for="unit in units" 
          :key="unit.id"
          class="unit-card"
          :class="{ 'completed': unit.isCompleted, 'locked': !selectedCourse.isUnlocked }"
          @click="handleUnitClick(unit)"
        >
          <div class="unit-icon">
            <i class="el-icon-document"></i>
              </div>
          <div class="unit-content">
            <h4>{{ unit.unitName }}</h4>
            <p>{{ unit.wordCount }}个单词</p>
            <div class="unit-status">
              <span v-if="unit.isCompleted" class="status-completed">
                <i class="el-icon-check"></i> 已完成
              </span>
              <span v-else class="status-pending">
                <i class="el-icon-time"></i> 待学习
              </span>
            </div>
          </div>
          <div class="unit-progress" v-if="unit.testRecord">
            <div class="progress-circle">
              <span>{{ unit.testRecord.score }}分</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 排行榜区域 -->
    <div class="leaderboard-section">
      <div class="section-title">
        <div class="title-bar"></div>
        <h3>阶段完成排行榜</h3>
        <p class="section-subtitle">看看谁最快完成学习，用时越短排名越高</p>
      </div>
      
      <StageLeaderboard :stages="courses" />
    </div>

    <!-- 单元学习弹窗 -->
    <UnitLearningDialog
      :visible.sync="showUnitDialog"
      :unit="selectedUnit"
      :course="selectedCourse"
      @learning-completed="handleLearningCompleted"
    />
  </div>
</template>

<script>
import { mapState } from "vuex";
import { CourseAPI } from "@/api/course";
import { homeApi } from "@/api/home";
import UnitLearningDialog from "@/components/UnitLearningDialog.vue";
import StageLeaderboard from "@/components/StageLeaderboard.vue";

export default {
  name: "WordBasics",
  components: {
    UnitLearningDialog,
    StageLeaderboard
  },
  data() {
    return {
      loading: false,
      courses: [],
      selectedCourseId: null,
      selectedCourse: {
        id: null,
        name: "请选择学习阶段",
        description: "选择学习阶段后开始学习",
        isUnlocked: false
      },
      units: [],
      showUnitDialog: false,
      selectedUnit: {
        id: null,
        unitName: "未知单元"
      }
    };
  },
  computed: {
    ...mapState("user", ["userInfo"]),
    userId() {
      return this.userInfo?.id || null;
    }
  },
  async mounted() {
    await this.loadCourses();
  },
  methods: {
    // 加载课程列表
    async loadCourses() {
      this.loading = true;
      try {
        const response = await CourseAPI.getMyCourseList();
        if (response && response.success) {
          this.courses = response.data || [];

          // 优先选中用户已选择的阶段（与首页/侧边栏保持一致）
          let targetCourse = null;
          try {
            const savedStage = JSON.parse(localStorage.getItem("selectedStage") || "null");
            if (savedStage && savedStage.id) {
              targetCourse = this.courses.find(c => c.id === savedStage.id);
            }
          } catch (e) {
            console.warn("恢复选中阶段失败:", e);
          }

          // 兜底：第一个已解锁的课程
          if (!targetCourse) {
            targetCourse = this.courses.find(course => course.isUnlocked);
          }

          if (targetCourse) {
            this.selectedCourseId = targetCourse.id;
            this.selectedCourse = targetCourse;
            await this.loadUnits(targetCourse.id);
          }
        } else {
          this.$message.error(response.message || "获取课程列表失败");
        }
      } catch (error) {
        console.error("加载课程失败:", error);
        this.$message.error("加载课程失败");
      } finally {
        this.loading = false;
      }
    },

    // 处理课程切换
    async handleCourseChange(courseId) {
      const course = this.courses.find(c => c.id === courseId);
      if (!course) return;

      this.selectedCourse = course;

      // 同步到全局：更新 localStorage，使首页/侧边栏等其他页面保持一致
      localStorage.setItem(
        "selectedStage",
        JSON.stringify({ id: course.id, name: course.name })
      );

      // 通知后端切换阶段（保存 current_stage_id + 重新分配今日单词），后台执行不阻塞单元加载
      if (this.userId) {
        homeApi.switchStage(this.userId, course.id).catch(e => {
          console.error("同步阶段到后端失败:", e);
        });
      }

      await this.loadUnits(courseId);
    },

    // 加载单元列表
    async loadUnits(stageId) {
      this.loading = true;
      try {
        const response = await CourseAPI.getUnits(stageId);
        if (response && response.success) {
          this.units = response.data.units || [];
        } else {
          this.$message.error(response.message || "获取单元列表失败");
          this.units = [];
        }
      } catch (error) {
        console.error("加载单元失败:", error);
        this.$message.error("加载单元失败");
        this.units = [];
      } finally {
        this.loading = false;
      }
    },

    // 处理单元点击
    handleUnitClick(unit) {
      if (!this.selectedCourse.isUnlocked) {
        this.$message.warning("请先解锁该课程");
        return;
      }
      
      this.selectedUnit = unit;
      this.showUnitDialog = true;
    },

    // 处理学习完成
    handleLearningCompleted(result) {
      this.showUnitDialog = false;
      this.$message.success(`学习完成！正确率：${result.accuracy}%`);
      // 重新加载单元列表以更新状态
      this.loadUnits(this.selectedCourse.id);
    },

    // 综合学习功能（暂时显示提示）
    startSmartDictation() {
      this.$message.info("智能听写功能开发中...");
    },

    startComparisonTest() {
      this.$message.info("对比测试功能开发中...");
    },

    startEndToEndTest() {
      this.$message.info("一测到底功能开发中...");
    },

    startAICloze() {
      this.$message.info("AI完形功能开发中...");
    }
  }
};
</script>

<style scoped>
.word-basics-page {
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
}

.page-header {
  text-align: center;
  margin-bottom: 32px;
}

.page-title {
  font-size: 28px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.page-title i {
  color: #ff8c00;
}

.page-subtitle {
  color: #7f8c8d;
  font-size: 16px;
}

.course-selection {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  margin-bottom: 32px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.course-info h2 {
  font-size: 20px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 8px;
}

.course-info p {
  color: #7f8c8d;
  font-size: 14px;
}

.course-switcher {
  min-width: 200px;
}

.section-title {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
}

.title-bar {
  width: 4px;
  height: 20px;
  background: #ff4757;
  margin-right: 12px;
  border-radius: 2px;
}

.section-title h3 {
  font-size: 18px;
  font-weight: 600;
  color: #2c3e50;
  margin: 0;
}

.comprehensive-learning {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  margin-bottom: 32px;
}

.learning-options {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
}

.option-card {
  display: flex;
  align-items: center;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.option-card:hover {
  background: #e9ecef;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.option-icon {
  width: 50px;
  height: 50px;
  background: #ff8c00;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 16px;
  font-size: 20px;
  color: white;
}

.option-content h4 {
  font-size: 16px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 4px;
}

.option-content p {
  font-size: 12px;
  color: #7f8c8d;
  margin: 0;
}

.unit-learning {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.units-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 20px;
}

.unit-card {
  background: #FFC107;
  border-radius: 12px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.unit-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(255, 193, 7, 0.3);
}

.unit-card.completed {
  background: #4CAF50;
}

.unit-card.completed:hover {
  box-shadow: 0 6px 20px rgba(76, 175, 80, 0.3);
}

.unit-card.locked {
  background: #95a5a6;
  cursor: not-allowed;
}

.unit-card.locked:hover {
  transform: none;
  box-shadow: none;
}

.unit-icon {
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
  font-size: 18px;
  color: white;
}

.unit-content h4 {
  font-size: 16px;
  font-weight: 600;
  color: white;
  margin-bottom: 4px;
}

.unit-content p {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 8px;
}

.unit-status {
  font-size: 12px;
}

.status-completed {
  color: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  gap: 4px;
}

.status-pending {
  color: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  gap: 4px;
}

.unit-progress {
  position: absolute;
  top: 12px;
  right: 12px;
}

.progress-circle {
  width: 30px;
  height: 30px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 600;
  color: white;
}

/* 排行榜区域样式 */
.leaderboard-section {
  margin-top: 48px;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 20px;
  padding: 32px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;
}

.leaderboard-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #667eea 0%, #764ba2 50%, #667eea 100%);
  background-size: 200% 100%;
  animation: gradientShift 3s ease-in-out infinite;
}

@keyframes gradientShift {
  0%, 100% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
}

.leaderboard-section .section-title {
  margin-bottom: 24px;
  text-align: center;
}

.leaderboard-section .section-title h3 {
  font-size: 24px;
  font-weight: 700;
  color: #2c3e50;
  /* margin-bottom: 8px; */
  position: relative;
}

.leaderboard-section .section-title h3::after {
  content: '🏆';
  position: absolute;
  right: -40px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 20px;
  animation: bounce 2s ease-in-out infinite;
}

@keyframes bounce {
  0%, 20%, 50%, 80%, 100% { transform: translateY(-50%); }
  40% { transform: translateY(-60%); }
  60% { transform: translateY(-55%); }
}

.section-subtitle {
  color: #6c757d;
  font-size: 15px;
  margin-left: 60px;
  text-align: center;
  font-weight: 500;
  background: rgba(255, 255, 255, 0.8);
  padding: 8px 16px;
  border-radius: 20px;
  display: inline-block;
  backdrop-filter: blur(10px);
}

@media (max-width: 768px) {
  .word-basics-page {
    padding: 16px;
  }

  .course-selection {
    flex-direction: column;
    gap: 16px;
    text-align: center;
  }

  .learning-options {
    grid-template-columns: 1fr;
  }

  .units-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>